import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { CalendarIcon, CreditCard } from 'lucide-react';
import { format } from 'date-fns';
import axios from 'axios';
import { toast } from 'sonner';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID;

export const Booking = () => {
  const { roomId } = useParams();
  const navigate = useNavigate();
  const { t, language } = useLanguage();

  const [room, setRoom] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);

  const [checkInDate, setCheckInDate] = useState(null);
  const [checkOutDate, setCheckOutDate] = useState(null);
  const [guestName, setGuestName] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [guestPhone, setGuestPhone] = useState('');
  const [guests, setGuests] = useState(1);
  const [paymentMethod, setPaymentMethod] = useState('stripe');
  const [currentBooking, setCurrentBooking] = useState(null);

  useEffect(() => {
    fetchRoom();
  }, [roomId]);

  const fetchRoom = async () => {
    try {
      const response = await axios.get(`${API}/rooms/${roomId}`);
      setRoom(response.data);
    } catch (error) {
      console.error('Error fetching room:', error);
      toast.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const calculateNights = () => {
    if (!checkInDate || !checkOutDate) return 0;
    const diff = checkOutDate - checkInDate;
    return Math.ceil(diff / (1000 * 60 * 60 * 24));
  };

  const calculateTotal = () => {
    if (!room) return 0;
    return room.price * calculateNights();
  };

  const validateForm = () => {
    if (!checkInDate || !checkOutDate || !guestName || !guestEmail || !guestPhone) {
      toast.error(language === 'it' ? 'Compila tutti i campi' : 'Please fill all fields');
      return false;
    }
    if (calculateNights() < 1) {
      toast.error(language === 'it' ? 'Seleziona date valide' : 'Please select valid dates');
      return false;
    }
    return true;
  };

  const createBooking = async () => {
    const bookingData = {
      room_id: roomId,
      guest_name: guestName,
      guest_email: guestEmail,
      guest_phone: guestPhone,
      check_in: format(checkInDate, 'yyyy-MM-dd'),
      check_out: format(checkOutDate, 'yyyy-MM-dd'),
      guests: guests,
      language: language
    };

    const bookingResponse = await axios.post(`${API}/bookings`, bookingData);
    return bookingResponse.data;
  };

  const handleStripeSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setProcessing(true);

    try {
      const booking = await createBooking();

      const paymentData = {
        booking_id: booking.booking_id,
        payment_method: 'stripe',
        origin_url: window.location.origin
      };

      const paymentResponse = await axios.post(`${API}/payment/stripe/checkout`, paymentData);
      window.location.href = paymentResponse.data.url;
    } catch (error) {
      console.error('Error creating booking:', error);
      toast.error(t('common.error'));
      setProcessing(false);
    }
  };

  const createPayPalOrder = async () => {
    if (!validateForm()) {
      throw new Error('Invalid form');
    }

    try {
      const booking = await createBooking();
      setCurrentBooking(booking);

      const paymentData = {
        booking_id: booking.booking_id,
        payment_method: 'paypal',
        origin_url: window.location.origin
      };

      const response = await axios.post(`${API}/payment/paypal/create-order`, paymentData);
      return response.data.order_id;
    } catch (error) {
      console.error('PayPal order creation error:', error);
      toast.error(t('common.error'));
      throw error;
    }
  };

  const onPayPalApprove = async (data) => {
    try {
      const response = await axios.post(`${API}/payment/paypal/capture-order`, {
        booking_id: currentBooking.booking_id,
        order_id: data.orderID
      });

      if (response.data.status === 'success') {
        navigate(`/payment-success?paypal_order_id=${data.orderID}`);
      } else {
        toast.error(t('common.error'));
      }
    } catch (error) {
      console.error('PayPal capture error:', error);
      toast.error(t('common.error'));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAF7] flex items-center justify-center">
        <p className="text-[#8A9A86] text-xl">{t('common.loading')}</p>
      </div>
    );
  }

  if (!room) {
    return (
      <div className="min-h-screen bg-[#FAFAF7] flex items-center justify-center">
        <p className="text-[#2C302E] text-xl">Room not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF7] py-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-5xl mx-auto">
        <h1 
          className="text-4xl sm:text-5xl font-light tracking-tight text-[#2C302E] mb-12 text-center"
          style={{fontFamily: 'Cormorant Garamond, serif'}}
          data-testid="booking-title"
        >
          {t('booking.title')}
        </h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <Card className="p-8 border border-[#E3E5DF]/40 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
              <form onSubmit={handleStripeSubmit} className="space-y-6">
                <div>
                  <h2 
                    className="text-2xl font-light text-[#2C302E] mb-6"
                    style={{fontFamily: 'Cormorant Garamond, serif'}}
                  >
                    {t('booking.guestDetails')}
                  </h2>

                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="name">{t('booking.name')}</Label>
                      <Input
                        id="name"
                        name="guest_name"
                        type="text"
                        placeholder="Mario Rossi"
                        data-testid="guest-name-input"
                        value={guestName}
                        onChange={(e) => setGuestName(e.target.value)}
                        className="mt-1"
                        required
                      />
                    </div>

                    <div>
                      <Label htmlFor="email">{t('booking.email')}</Label>
                      <Input
                        id="email"
                        name="guest_email"
                        type="email"
                        placeholder="mario.rossi@example.com"
                        data-testid="guest-email-input"
                        value={guestEmail}
                        onChange={(e) => setGuestEmail(e.target.value)}
                        className="mt-1"
                        required
                      />
                    </div>

                    <div>
                      <Label htmlFor="phone">{t('booking.phone')}</Label>
                      <Input
                        id="phone"
                        name="guest_phone"
                        type="tel"
                        placeholder="+39 123 456 7890"
                        data-testid="guest-phone-input"
                        value={guestPhone}
                        onChange={(e) => setGuestPhone(e.target.value)}
                        className="mt-1"
                        required
                      />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <Label>{t('booking.checkIn')}</Label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              data-testid="check-in-date-button"
                              variant="outline"
                              className="w-full justify-start text-left font-normal mt-1"
                            >
                              <CalendarIcon className="mr-2 h-4 w-4" />
                              {checkInDate ? format(checkInDate, 'PPP') : <span>Pick a date</span>}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <Calendar
                              mode="single"
                              selected={checkInDate}
                              onSelect={setCheckInDate}
                              initialFocus
                              disabled={(date) => date < new Date()}
                            />
                          </PopoverContent>
                        </Popover>
                      </div>

                      <div>
                        <Label>{t('booking.checkOut')}</Label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              data-testid="check-out-date-button"
                              variant="outline"
                              className="w-full justify-start text-left font-normal mt-1"
                            >
                              <CalendarIcon className="mr-2 h-4 w-4" />
                              {checkOutDate ? format(checkOutDate, 'PPP') : <span>Pick a date</span>}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <Calendar
                              mode="single"
                              selected={checkOutDate}
                              onSelect={setCheckOutDate}
                              initialFocus
                              disabled={(date) => !checkInDate || date <= checkInDate}
                            />
                          </PopoverContent>
                        </Popover>
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="guests">{t('booking.guests')}</Label>
                      <Input
                        id="guests"
                        type="number"
                        min="1"
                        max={room.max_guests}
                        data-testid="guests-input"
                        value={guests}
                        onChange={(e) => setGuests(parseInt(e.target.value))}
                        className="mt-1"
                        required
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <h2 
                    className="text-2xl font-light text-[#2C302E] mb-4"
                    style={{fontFamily: 'Cormorant Garamond, serif'}}
                  >
                    {t('booking.selectPayment')}
                  </h2>

                  <RadioGroup value={paymentMethod} onValueChange={setPaymentMethod}>
                    <div className="flex items-center space-x-2 p-4 border border-[#E3E5DF] rounded-lg mb-3">
                      <RadioGroupItem value="stripe" id="payment-stripe" data-testid="payment-method-stripe" />
                      <Label htmlFor="payment-stripe" className="flex items-center gap-2 cursor-pointer">
                        <CreditCard size={20} className="text-[#8A9A86]" />
                        {t('booking.creditCard')}
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2 p-4 border border-[#E3E5DF] rounded-lg">
                      <RadioGroupItem value="paypal" id="payment-paypal" data-testid="payment-method-paypal" />
                      <Label htmlFor="payment-paypal" className="flex items-center gap-2 cursor-pointer">
                        <span className="font-bold"><span className="text-[#0070ba]">Pay</span><span className="text-[#003087]">Pal</span></span>
                      </Label>
                    </div>
                  </RadioGroup>
                </div>

                {paymentMethod === 'stripe' ? (
                  <Button
                    type="submit"
                    data-testid="submit-booking-button"
                    disabled={processing}
                    className="w-full bg-[#8A9A86] hover:bg-[#7A8A76] text-white rounded-full py-6 text-lg"
                  >
                    {processing ? t('booking.processing') : t('booking.continueToPayment')}
                  </Button>
                ) : (
                  <div data-testid="paypal-buttons-container" className="space-y-3">
                    <div className="bg-[#F0F2EB] border border-[#E3E5DF] rounded-lg p-4 text-center">
                      <p 
                        className="text-sm text-[#2C302E]/80"
                        style={{fontFamily: 'Manrope, sans-serif'}}
                      >
                        {language === 'it' 
                          ? '👇 Clicca sul pulsante PayPal qui sotto per completare la prenotazione' 
                          : '👇 Click the PayPal button below to complete your booking'}
                      </p>
                    </div>
                    {PAYPAL_CLIENT_ID ? (
                      <div className="min-h-[100px]">
                        <PayPalScriptProvider options={{
                          "client-id": PAYPAL_CLIENT_ID,
                          currency: "EUR",
                          intent: "capture",
                          components: "buttons"
                        }}>
                          <PayPalButtons
                            style={{ layout: "vertical", color: "gold", shape: "pill", height: 55 }}
                            createOrder={createPayPalOrder}
                            onApprove={onPayPalApprove}
                            onError={(err) => {
                              console.error('PayPal error:', err);
                              toast.error(t('common.error'));
                            }}
                          />
                        </PayPalScriptProvider>
                      </div>
                    ) : (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
                        <p className="text-sm text-red-700">
                          {language === 'it' 
                            ? 'PayPal non disponibile. Seleziona Carta di Credito.' 
                            : 'PayPal unavailable. Please select Credit Card.'}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </form>
            </Card>
          </div>

          <div className="lg:col-span-1">
            <Card className="p-8 border border-[#E3E5DF]/40 shadow-[0_8px_30px_rgb(0,0,0,0.04)] sticky top-8">
              <h2 
                className="text-2xl font-light text-[#2C302E] mb-6"
                style={{fontFamily: 'Cormorant Garamond, serif'}}
              >
                {t('booking.bookingSummary')}
              </h2>

              <div className="space-y-4">
                <div className="aspect-video rounded-lg overflow-hidden">
                  <img
                    src={room.image_url}
                    alt={language === 'it' ? room.name_it : room.name_en}
                    className="w-full h-full object-cover"
                  />
                </div>

                <div>
                  <h3 
                    className="text-xl font-light text-[#2C302E]"
                    style={{fontFamily: 'Cormorant Garamond, serif'}}
                  >
                    {language === 'it' ? room.name_it : room.name_en}
                  </h3>
                </div>

                <div className="border-t border-[#E3E5DF] pt-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-[#2C302E]/70">€{room.price} × {calculateNights()} {t('booking.nights')}</span>
                    <span className="text-[#2C302E]">€{calculateTotal().toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between font-medium text-lg pt-2 border-t border-[#E3E5DF]">
                    <span className="text-[#2C302E]">{t('booking.total')}</span>
                    <span className="text-[#8A9A86]" data-testid="total-price">€{calculateTotal().toFixed(2)}</span>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};
